import { useApiEssentials } from '../../../shared/hooks/useApiEssentials';
import { useQuery } from '@tanstack/react-query';
import { get, post } from '../../../shared/api/apiService';
import { useMemo, useState, useEffect } from 'react';
import { ProductData } from '../types';

export const useProducts = () => {
  const { apiUrl, getIdToken, isSignedIn } = useApiEssentials('/resource-broker/api/v2');

  return useQuery<{ elements: ProductData[] }, Error>({
    queryKey: ['products'],
    queryFn: async () => {
      const idToken = isSignedIn ? await getIdToken() : '';
      return post<{ elements: ProductData[] }>(
        '/resources/page',
        {
          page: 0,
          limit: 100,
        },
        apiUrl,
        idToken,
      );
    },
    enabled: !!apiUrl,
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });
};

export const usePagination = <T,>(
  items: T[],
  itemsPerPage: number,
  resetDeps: any[] = [],
) => {
  const [visibleCount, setVisibleCount] = useState(itemsPerPage);

  useEffect(() => {
    setVisibleCount(itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage, ...resetDeps]);

  const hasMoreItems = visibleCount < items.length;
  const itemsToShow = hasMoreItems
    ? Math.min(visibleCount, items.length)
    : items.length;

  const visibleItems = items.slice(0, itemsToShow);
  const showLoadMore = hasMoreItems && items.length > itemsPerPage;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + itemsPerPage);
  };

  return { visibleItems, showLoadMore, handleLoadMore };
};

export const useFilterQuantities = () => {
  const { data: productsData } = useProducts();

  const quantities = useMemo(() => {
    if (!productsData?.elements) return {};

    const apiProducts = productsData.elements.filter(
      item => item.type === 'api',
    );

    const typeCounts = apiProducts.reduce((acc, product) => {
      const tag = product.tag;
      if (!acc[tag]) {
        acc[tag] = 0;
      }
      acc[tag]++;
      return acc;
    }, {} as Record<string, number>);
    return typeCounts;
  }, [productsData?.elements]);

  return { quantities };
};

export const useProduct = (id: string) => {
  const { apiUrl, getIdToken, isSignedIn } = useApiEssentials('/resource-broker/api/v2');

  return useQuery<ProductData, Error>({
    queryKey: ['product', id],
    queryFn: async () => {
      const idToken = await getIdToken();
      return get<ProductData>(`/resources/${id}`, apiUrl, idToken);
    },
    enabled: !!id && !!apiUrl && isSignedIn,
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });
};
